// src/pages/Login.js
import { ArrowLeft, ArrowRight, Mail, Phone } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from "react-helmet";
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import axios from "../utils/axiosHandler";

const Login = () => {
  const [identifier, setIdentifier] = useState('');
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(60);
  const [step, setStep] = useState(1); // 1: identifier input, 2: verification code, 3: registration form
  const [identifierType, setIdentifierType] = useState(''); // 'email' or 'phone'
  const [animatedFields, setAnimatedFields] = useState({});
  const [registrationFlow, setRegistrationFlow] = useState(false);
  const [verifiedIdentifier, setVerifiedIdentifier] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const navigate = useNavigate();
  const location = useLocation(); 
  const { login } = useAuth();
  const { config } = useConfig();
  const otpRefs = useRef([]);

  // Countdown timer for OTP resend functionality
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Auto-focus first input when component mounts or step changes
  useEffect(() => {
    if (step === 2 && identifier) {
      setTimeout(() => {
        otpRefs.current[0]?.focus();
      }, 100);
    }
  }, [step, identifier]);

  // Trigger shake animation for invalid fields
  const animateField = (fieldName) => {
    setAnimatedFields(prev => ({ ...prev, [fieldName]: true }));
    setTimeout(() => {
      setAnimatedFields(prev => ({ ...prev, [fieldName]: false }));
    }, 500);
  };

  // Return appropriate icon based on input content
  const getIdentifierIcon = () => {
    if (!identifier.trim()) return Mail; // Default to email icon when empty
    
    if (identifier.includes('@')) return Mail; // Email format detected
    
    const phonePattern = /^[\+\d\s\-\(\)]+$/;
    if (phonePattern.test(identifier.trim())) return Phone; // Phone format detected
    
    return Mail; // Default fallback
  };

  const sanitizeInput = (input) => {
    // Remove leading/trailing whitespace and normalize spaces
    return input.trim().replace(/\s+/g, ' ');
  };

  const determineIdentifierType = (value) => {
    // Simple regex for email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(value)) {
      return 'email';
    }
    
    // Simple regex for phone validation (numbers, possibly with +, -, or spaces)
    const phoneRegex = /^[+\d\s-]{8,}$/;
    if (phoneRegex.test(value)) {
      return 'phone';
    }
    
    return '';
  };

  const validateIdentifier = () => {
    const cleanIdentifier = sanitizeInput(identifier);
    const type = determineIdentifierType(cleanIdentifier);
    
    if (!type) {
      setError('Please enter a valid email address or phone number');
      animateField('identifier');
      return false;
    }

    setIdentifierType(type);
    return true;
  };

  // Handle changes to individual OTP input fields
  const handleOtpChange = (index, value) => {
    // Handle auto-fill of entire code (when browser fills all at once)
    if (value.length > 1) {
      const numbers = value.replace(/\D/g, '');
      if (numbers.length >= 6) {
        const codeArray = numbers.slice(0, 6).split('');
        setOtpCode(codeArray);
        
        // Auto-verify the complete code
        setTimeout(() => {
          handleOtpVerification(codeArray.join(''));
        }, 300);
        return;
      }
    }
    
    // Only allow numeric input
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otpCode];
    newOtp[index] = value;
    setOtpCode(newOtp);

    // Auto-advance to next field when digit entered
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (newOtp.every(digit => digit !== '') && newOtp.join('').length === 6) {
      handleOtpVerification(newOtp.join(''));
    }
  };

  // Handle backspace navigation between OTP fields
  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste event
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData?.getData('text') || '';
    const numbers = pastedText.replace(/\D/g, '');
    
    if (numbers.length >= 6) {
      const codeArray = numbers.slice(0, 6).split('');
      setOtpCode(codeArray);
      
      // Auto-verify pasted code
      setTimeout(() => {
        handleOtpVerification(codeArray.join(''));
      }, 300);
    }
  };

  const handleOtpVerification = async (code) => {
    const verificationCode = code || otpCode.join('');
    
    if (verificationCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const cleanIdentifier = sanitizeInput(identifier);
      const { data } = await axios.post('/api/login/verify', {
        identifier: cleanIdentifier,
        verificationCode: verificationCode
      }, {
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      // Check if this is a registration flow
      if (data.registrationFlow) {
        setStep(3); // Move to registration form
        setVerifiedIdentifier(data.verifiedIdentifier);
        setIdentifierType(data.identifierType);
        return;
      }
      
      // Regular login flow
      console.log('✅ Login verification successful:', {
        user: data.user,
        token: data.token ? 'Token received' : 'No token'
      });
      
      await login(data.user, data.token, true); // Pass noRedirect: true to handle redirect manually

      // Check if there are items in local cart
      const localCart = JSON.parse(localStorage.getItem('localCart') || '[]');
      const hasLocalCartItems = localCart.length > 0;

      // Navigate based on redirect location or cart status
      if (location.state?.from === '/cart' || hasLocalCartItems) {
        navigate('/cart');
        console.log('from cart');
        window.URL('/cart');
      } else {
        navigate(location.state?.from || '/');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 
                         err.response?.data?.message || 
                         'Failed to verify code';
      setError(errorMessage);
      // Reset OTP inputs on error
      setOtpCode(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;

    setError('');
    setResendLoading(true);

    try {
      const cleanIdentifier = sanitizeInput(identifier);
      const { data } = await axios.post('/api/login', {
        identifier: cleanIdentifier
      }, {
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (data.requiresVerification) {
        setResendCooldown(60);
        setError('');
      } else {
        setError('Unexpected response from server');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 
                         err.response?.data?.message || 
                         'Failed to resend code';
      setError(errorMessage);
    } finally {
      setResendLoading(false);
    }
  };

  const handleIdentifierSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
  
    if (!validateIdentifier()) {
      setLoading(false);
      return;
    }
  
    try {
      const cleanIdentifier = sanitizeInput(identifier);
      const { data } = await axios.post('/api/login', {
        identifier: cleanIdentifier
      }, {
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (data.requiresVerification) {
        setStep(2); // Move to verification code step
        setError('');
        setResendCooldown(60); // Start cooldown for resend
        
        // Check if this is a registration flow
        if (data.registrationFlow) {
          setRegistrationFlow(true);
          setVerifiedIdentifier(cleanIdentifier);
          setIdentifierType(data.identifierType);
          
          // Pre-fill the form based on identifier type
          if (data.identifierType === 'email') {
            setEmail(cleanIdentifier);
          } else {
            setPhone(cleanIdentifier);
          }
        }
      } else {
        setError('Unexpected response from server');
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // Check if the error is about user not existing
      if (error.response && 
          error.response.status === 404 && 
          error.response.data && 
          error.response.data.message === 'User not found') {
        
        // Navigate to register page with the identifier pre-filled
        const cleanIdentifier = sanitizeInput(identifier);
        navigate(`/register?identifier=${encodeURIComponent(cleanIdentifier)}`);
      } else {
        // Show the actual error message
        const errorMessage = error.response?.data?.error || 
                           error.response?.data?.message || 
                           'Failed to send verification code';
        setError(errorMessage);
        animateField('identifier');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToIdentifier = () => {
    setStep(1);
    setOtpCode(['', '', '', '', '', '']);
    setError('');
    setResendCooldown(0);
    setRegistrationFlow(false);
    setVerifiedIdentifier('');
    setEmail('');
    setPhone('');
    setFirstName('');
    setLastName('');
    setCompany('');
  };

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate required fields
    if (!firstName.trim() || !lastName.trim()) {
      setError('First name and last name are required');
      setLoading(false);
      return;
    }

    if (!email.trim() || !phone.trim()) {
      setError('Both email and phone are required');
      setLoading(false);
      return;
    }

    try {
      const { data } = await axios.post('/api/login/complete-registration', {
        verifiedIdentifier,
        identifierType,
        email: email.trim(),
        phone: phone.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        company: company.trim()
      }, {
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      console.log('✅ Registration response received:', {
        user: data.user,
        token: data.token ? 'Token received' : 'No token',
        success: data.success
      });
      
      await login(data.user, data.token, true); // Pass noRedirect: true to handle redirect manually

      // Check if there are items in local cart
      const localCart = JSON.parse(localStorage.getItem('localCart') || '[]');
      const hasLocalCartItems = localCart.length > 0;

      // Navigate based on redirect location or cart status
      if (location.state?.from === '/cart' || hasLocalCartItems) {
        navigate('/cart');
        console.log('from cart');
        window.URL('/cart');
      } else {
        navigate(location.state?.from || '/');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 
                         err.response?.data?.message || 
                         'Failed to complete registration';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Get logo URL from config with fallback
  const getLogoUrl = () => {
    if (config?.logoUrl) {
      console.log('🔍 Login: Original logo URL from config:', config.logoUrl);
      
      // If it's already a full URL, use it as is
      if (config.logoUrl.startsWith('http://') || config.logoUrl.startsWith('https://')) {
        console.log('🔍 Login: Using full URL as is:', config.logoUrl);
        return config.logoUrl;
      }
      
      // If it's an upload path, convert to API path
      if (config.logoUrl.startsWith('/upload/')) {
        const apiUrl = `/api${config.logoUrl}`;
        console.log('🔍 Login: Converting upload path to API path:', config.logoUrl, '→', apiUrl);
        return apiUrl;
      }
      
      // If it's already an API path, use as is
      if (config.logoUrl.startsWith('/api/upload/')) {
        console.log('🔍 Login: Using API path as is:', config.logoUrl);
        return config.logoUrl;
      }
      
      // If it's a relative path, prepend the base URL
      const finalUrl = config.logoUrl.startsWith('/') ? config.logoUrl : `/${config.logoUrl}`;
      console.log('🔍 Login: Using relative path:', finalUrl);
      return finalUrl;
    }
    console.log('🔍 Login: No logo URL found in config');
    return null;
  };

  const logoUrl = getLogoUrl();

  return (    
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {(process.env.NODE_ENV === "development" || window.location.hostname === "dev.bagbox.ca") && (
        <Helmet>
            <meta name="robots" content="noindex, nofollow" />
        </Helmet>
      )}

      {/* Animated Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-pulse" />
        <div className="absolute top-3/4 right-1/4 w-80 h-80 bg-[#19c8c7]/10 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-3/4 w-64 h-64 bg-[#64e8e4]/5 rounded-full blur-3xl animate-pulse delay-500" />
      </div>

      <div className="max-w-md w-full space-y-8 relative z-10">
        
        {/* Logo Section - Outside the form container */}
        {logoUrl && (
          <div className="text-center mb-8">
            <div className="flex justify-center">
              <img
                src={logoUrl}
                alt={config?.siteName || 'Company Logo'}
                className="max-h-20 max-w-full object-contain"
                onError={(e) => {
                  console.warn('❌ Login: Failed to load logo:', logoUrl);
                  e.target.style.display = 'none';
                }}
                onLoad={() => {
                  console.log('✅ Login: Logo loaded successfully:', logoUrl);
                }}
              />
            </div>
          </div>
        )}

        <div className="bg-white/95 backdrop-blur-xl rounded-xl shadow-2xl p-8 border border-white/20 transform transition-all duration-500">
          
          {/* Header */}
          {/* <div className="text-center mb-8">
            <p className="text-gray-600 text-sm">
              {step === 1 ? '' : `Code sent to ${identifierType === 'email' ? 'your email' : 'your phone'}`}
            </p>
          </div> */}

          {/* Step indicator */}
          {/* {step === 2 && (
            <div className="mb-6">
              <div className="flex items-center justify-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-[#5347ce]"></div>
                <div className="w-3 h-3 rounded-full bg-[#5347ce]"></div>
              </div>
            </div>
          )} */}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-400 text-red-700 rounded-xl animate-shake">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-red-400 rounded-full mr-3 animate-pulse"></div>
                {error}
              </div>
            </div>
          )}
        
          {step === 1 ? (
            <form onSubmit={handleIdentifierSubmit} className="space-y-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-[#5347ce] mb-3">
                  Email / Phone
                </label>
                <div className={`relative transition-all duration-300 ${animatedFields.identifier ? 'animate-shake' : ''}`}>
                  <div className="absolute inset-0 bg-gradient-to-r from-[#5347ce]/10 to-[#4795fe]/10 rounded-xl blur opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  {(() => {
                    const IconComponent = getIdentifierIcon();
                    return <IconComponent className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#5347ce] w-5 h-5 z-10" />;
                  })()}
                  <input
                    type="text"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    className="relative w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-[#5347ce]/20 focus:border-[#5347ce] transition-all duration-300 text-sm font-medium bg-white/80 backdrop-blur-sm hover:bg-white/90 group"
                    placeholder="Enter your email or phone number"
                    required
                    maxLength={100}
                    autoComplete="username"
                    disabled={loading}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !identifier.trim()}
                className="w-full bg-gradient-to-r from-[#5347ce] to-[#4795fe] text-white px-6 py-4 rounded-xl font-bold text-lg hover:from-[#877cfd] hover:to-[#19c8c7] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-300 transform hover:scale-[1.02] hover:shadow-2xl group relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-white/30 border-t-white mr-3"></div>
                    <span className="relative z-10">Sending Code...</span>
                  </>
                ) : (
                  <>
                    <span className="relative z-10">Continue</span>
                    <ArrowRight className="ml-3 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300 relative z-10" />
                  </>
                )}
              </button>
            </form>
          ) : step === 2 ? (
            <div className="space-y-8">
              {/* OTP Input Fields */}
              <div>
                <label className="block text-sm font-semibold text-[#4795fe] mb-6 text-center">
                  Enter verification code
                </label>
                <div className="flex justify-center space-x-3">
                  {otpCode.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => {
                        otpRefs.current[index] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      onPaste={handleOtpPaste}
                      className="w-12 h-12 text-center text-2xl font-bold border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-[#4795fe]/20 focus:border-[#4795fe] transition-all duration-300 bg-white/80 backdrop-blur-sm hover:bg-white/90 hover:scale-105"
                      disabled={loading}
                      autoComplete="one-time-code"
                    />
                  ))}
                </div>
              </div>

              {/* Resend Code Button */}
              <div className="text-center">
                <button
                  type="button"
                  style={{textDecoration:'none', transform:'none', boxShadow:'none'}}
                  onClick={handleResendCode}
                  disabled={resendCooldown > 0 || resendLoading}
                  className="text-sm font-medium text-[#4795fe] hover:text-[#19c8c7] disabled:text-[#64748b] disabled:cursor-not-allowed transition-colors duration-300 underline hover:no-underline"
                >
                  {resendLoading ? (
                    <span className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#4795fe]/30 border-t-[#4795fe] mr-2"></div>
                      Resending...
                    </span>
                  ) : resendCooldown > 0 ? (
                    <span>Resend in {resendCooldown}s</span>
                  ) : (
                    'Resend code'
                  )}
                </button>
              </div>

              {/* Back to Login */}
              <button
                type="button"
                style={{textDecoration:'none', transform:'none', boxShadow:'none'}}

                onClick={handleBackToIdentifier}
                className="w-full text-[#64748b] hover:text-[#5347ce] flex items-center justify-center transition-colors duration-300 font-medium"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Change {identifierType}
              </button>

              {/* Loading Indicator */}
              {loading && (
                <div className="text-center">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#4795fe]/30 border-t-[#4795fe]"></div>
                    <span className="text-[#4795fe] font-medium">Verifying...</span>
                  </div>
                </div>
              )}
            </div>
          ) : step === 3 ? (
            <form onSubmit={handleRegistrationSubmit} className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-[#5347ce] mb-2">
                  Complete Your Registration
                </h3>
                <p className="text-sm text-gray-600">
                  We've verified your {identifierType === 'email' ? 'email' : 'phone'}. Please provide the remaining information.
                </p>
              </div>

              {/* Email field (if phone was provided initially) */}
              {identifierType === 'phone' && (
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-[#5347ce] mb-3">
                    Email Address *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#5347ce] w-5 h-5 z-10" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-[#5347ce]/20 focus:border-[#5347ce] transition-all duration-300 text-sm font-medium bg-white/80 backdrop-blur-sm hover:bg-white/90"
                      placeholder="Enter your email address"
                      required
                      maxLength={100}
                      autoComplete="email"
                      disabled={loading}
                    />
                  </div>
                </div>
              )}

              {/* Phone field (if email was provided initially) */}
              {identifierType === 'email' && (
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-[#5347ce] mb-3">
                    Phone Number *
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#5347ce] w-5 h-5 z-10" />
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-[#5347ce]/20 focus:border-[#5347ce] transition-all duration-300 text-sm font-medium bg-white/80 backdrop-blur-sm hover:bg-white/90"
                      placeholder="Enter your phone number"
                      required
                      maxLength={20}
                      autoComplete="tel"
                      disabled={loading}
                    />
                  </div>
                </div>
              )}

              {/* Name fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-[#5347ce] mb-3">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-[#5347ce]/20 focus:border-[#5347ce] transition-all duration-300 text-sm font-medium bg-white/80 backdrop-blur-sm hover:bg-white/90"
                    placeholder="First name"
                    required
                    maxLength={50}
                    autoComplete="given-name"
                    disabled={loading}
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-[#5347ce] mb-3">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-[#5347ce]/20 focus:border-[#5347ce] transition-all duration-300 text-sm font-medium bg-white/80 backdrop-blur-sm hover:bg-white/90"
                    placeholder="Last name"
                    required
                    maxLength={50}
                    autoComplete="family-name"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Company field (optional) */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-[#5347ce] mb-3">
                  Company Name (Optional)
                </label>
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-[#5347ce]/20 focus:border-[#5347ce] transition-all duration-300 text-sm font-medium bg-white/80 backdrop-blur-sm hover:bg-white/90"
                  placeholder="Company name"
                  maxLength={100}
                  autoComplete="organization"
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                disabled={loading || !firstName.trim() || !lastName.trim() || !email.trim() || !phone.trim()}
                className="w-full bg-gradient-to-r from-[#5347ce] to-[#4795fe] text-white px-6 py-4 rounded-xl font-bold text-lg hover:from-[#877cfd] hover:to-[#19c8c7] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-300 transform hover:scale-[1.02] hover:shadow-2xl group relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-white/30 border-t-white mr-3"></div>
                    <span className="relative z-10">Creating Account...</span>
                  </>
                ) : (
                  <>
                    <span className="relative z-10">Create Account</span>
                    <ArrowRight className="ml-3 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300 relative z-10" />
                  </>
                )}
              </button>

              {/* Back to Login */}
              <button
                type="button"
                style={{textDecoration:'none', transform:'none', boxShadow:'none'}}
                onClick={handleBackToIdentifier}
                className="w-full text-[#64748b] hover:text-[#5347ce] flex items-center justify-center transition-colors duration-300 font-medium"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to Login
              </button>
            </form>
          ) : null}
        </div>
      </div>

      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default Login;











// // before, have password and forgot password
// // src/pages/Login.js
// import { Eye, EyeOff } from 'lucide-react';
// import React, { useState } from 'react';
// import { Helmet } from "react-helmet";
// import { useLocation, useNavigate } from 'react-router-dom'; // Add useLocation
// import { useAuth } from '../context/AuthContext';
// import { useConfig } from '../context/ConfigContext';
// import axios from "../utils/axiosHandler";

// const Login = () => {
//   const [identifier, setIdentifier] = useState('');
//   const [password, setPassword] = useState('');
//   const [error, setError] = useState('');
//   const navigate = useNavigate();
//   const location = useLocation(); 
//   const { login } = useAuth();
//   const [showPassword, setShowPassword] = useState(false);
//   const { config } = useConfig();

//   const sanitizeInput = (input) => {
//     // Remove leading/trailing whitespace and normalize spaces
//     return input.trim().replace(/\s+/g, ' ');
//   };

//   const validateInput = () => {
//     const cleanIdentifier = sanitizeInput(identifier);
    
//     // Basic email validation if identifier contains @
//     if (cleanIdentifier.includes('@')) {
//       const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//       if (!emailRegex.test(cleanIdentifier)) {
//         setError('Please enter a valid email address');
//         return false;
//       }
//     } else {
//       // Phone number validation (basic example - adjust according to your needs)
//       const phoneRegex = /^\+?[\d\s-]{8,}$/;
//       if (!phoneRegex.test(cleanIdentifier)) {
//         setError('Please enter a valid phone number');
//         return false;
//       }
//     }

//     if (password.length < 8) {
//       setError('Password must be at least 8 characters long');
//       return false;
//     }

//     return true;
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setError('');
  
//     if (!validateInput()) {
//       return;
//     }
  
//     try {
//       const cleanIdentifier = sanitizeInput(identifier);
//       const { data } = await axios.post('/api/login', {
//         identifier: cleanIdentifier,
//         password: password // Never trim passwords as spaces might be intentional
//       }, {
//         headers: {
//           'Content-Type': 'application/json',
//         }
//       });
      
//       await login(data.user, data.token);
  
//       // Check if there are items in local cart
//       const localCart = JSON.parse(localStorage.getItem('localCart') || '[]');
//       const hasLocalCartItems = localCart.length > 0;
  
//       // Determine redirect path
//       const redirectPath = location.state?.from || '/cart';
//       console.log('Redirecting to:', redirectPath);
  
//       // Navigate with replace to prevent back-button issues
//       navigate(redirectPath, { replace: true });
  
//       // Navigate based on redirect location or cart status
//       if (location.state?.from === '/cart' || hasLocalCartItems) {
//         navigate('/cart');
//         console.log('from cart');
//         window.URL('/cart');
        
//       } else {
//         navigate(location.state?.from || '/');
//       }
//     } catch (error) {
//       // Check if the error is about user not existing
//       if (error.response && 
//           error.response.status === 404 && 
//           error.response.data && 
//           error.response.data.message === 'User not found') {
        
//         // Navigate to register page with the identifier pre-filled
//         const cleanIdentifier = sanitizeInput(identifier);
//         navigate(`/register?identifier=${encodeURIComponent(cleanIdentifier)}`);
//       } else {
//         // Generic error message for other errors
//         setError('Invalid credentials');
//       }
//     }
//   };

//   return (    
//     <div className="w-full p-4" >
    
//       {(process.env.NODE_ENV === "development" || window.location.hostname === "dev.bagbox.ca") && (
//         <Helmet>
//             <meta name="robots" content="noindex, nofollow" />
//         </Helmet>
//       )}

//       <div
//         className="  max-w-md mx-auto bg-white rounded-lg shadow-md p-6 md:min-w-[700px]" 
//         style={{marginTop: 100+'px', marginBottom: 100+'px'}}
//       >
//         <h2 className="text-2xl font-bold mb-4" style={{ color: config.colors.textPrimary }}>Login</h2>
//         {error && <p className="text-red-500 mb-4">{error}</p>}
//         <form onSubmit={handleSubmit} autoComplete="off">
//           <div className="mb-4">
//             <label className="block text-gray-700 mb-2">Email or Phone Number</label>
//             <input
//               type="text"
//               value={identifier}
//               onChange={(e) => setIdentifier(e.target.value)}
//               className="w-full p-2 border rounded-md focus:outline-none transition-colors"
//               style={{ borderColor: `${config.colors.textSecondary}40` }}
//               onFocus={(e) => {
//                 e.target.style.borderColor = config.colors.primary;
//                 e.target.style.boxShadow = `0 0 0 2px ${config.colors.primary}30`;
//                 e.target.style.outline = 'none';
//               }}
//               onBlur={(e) => {
//                 e.target.style.borderColor = `${config.colors.textSecondary}40`;
//                 e.target.style.boxShadow = 'none';
//               }}
//               placeholder="Enter email or phone number"
//               required
//               maxLength={100}
//               autoComplete="username"
//             />
//           </div>
//           <div className="mb-4">
//             <label className="block text-gray-700 mb-2">Password</label>
//             <div className="relative">
//               <input
//                 type={showPassword ? "text" : "password"}
//                 value={password}
//                 onChange={(e) => setPassword(e.target.value)}
//                 className="w-full p-2 border rounded-md focus:outline-none transition-colors"
//                 style={{ borderColor: `${config.colors.textSecondary}40` }}
//                 onFocus={(e) => {
//                   e.target.style.borderColor = config.colors.primary;
//                   e.target.style.boxShadow = `0 0 0 2px ${config.colors.primary}30`;
//                   e.target.style.outline = 'none';
//                 }}
//                 onBlur={(e) => {
//                   e.target.style.borderColor = `${config.colors.textSecondary}40`;
//                   e.target.style.boxShadow = 'none';
//                 }}
//                 placeholder="Enter your passwprd"
//                 required
//                 maxLength={128}
//               />
//               <button
//                 type="button"
//                 onClick={() => setShowPassword(!showPassword)}
//                 style={{ 
//                   boxShadow: 'none', 
//                   border: 'none', 
//                   background: 'transparent',
//                   transform: 'none',
//                   marginTop: -20+'px',
//                 }}
//                 className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
//               >
//                 {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
//               </button>
//             </div>
//           </div>
//           <button
//             type="submit"
//             className="w-full text-white px-4 py-2 rounded"
//             style={{ 
//               backgroundColor: config.colors.primary,
//               transition: 'background-color 0.3s ease'
//             }}
//             onMouseOver={(e) => {
//               e.currentTarget.style.backgroundColor = `${config.colors.primary}DD`;
//             }}
//             onMouseOut={(e) => {
//               e.currentTarget.style.backgroundColor = config.colors.primary;
//             }}
//           >
//             Login
//           </button>
//         </form>
//         <div className="mt-6 flex flex-col sm:flex-row justify-between items-center">
//           <a
//             href="/forgot-password"
//             className="text-sm hover:underline mb-2 sm:mb-0"
//             style={{ 
//               color: config.colors.primary,
//               ":hover": { color: `${config.colors.primary}DD` }
//             }}
//           >
//             Forgot your password?
//           </a>
//           <a
//             href="/register"
//             lassName="text-sm hover:underline"
//             style={{ 
//               color: config.colors.primary,
//               ":hover": { color: `${config.colors.primary}DD` }
//             }}
//           >
//             Don't have an account? Register now
//           </a>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Login;








